/**
 * Offline Indicator Component
 *
 * Displays a banner/toast when the user is offline and shows
 * sync status when syncing pending changes.
 */

import { useState, useEffect, useCallback } from 'react';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { useSyncStatus, useSyncStatusText } from '../../hooks/useSyncStatus';
import styles from './OfflineIndicator.module.css';

export function OfflineIndicator() {
  const { isOnline } = useOnlineStatus();
  const { status, total, completed, triggerSync } = useSyncStatus();
  const statusText = useSyncStatusText();

  // Don't show anything if online and not syncing
  if (isOnline && status === 'idle' && total === 0) {
    return null;
  }

  const showOfflineBanner = !isOnline;
  const showSyncBanner = isOnline && (status === 'syncing' || status === 'error');

  if (!showOfflineBanner && !showSyncBanner) {
    return null;
  }

  return (
    <div className={styles.container}>
      {showOfflineBanner && (
        <div className={`${styles.banner} ${styles.offline}`}>
          <svg className={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="1" y1="1" x2="23" y2="23" />
            <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
            <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
            <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
            <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
            <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
            <line x1="12" y1="20" x2="12.01" y2="20" />
          </svg>
          <span className={styles.text}>
            You are offline. Changes will be saved locally and synced when you reconnect.
          </span>
          <span className={styles.indicator} />
        </div>
      )}

      {showSyncBanner && (
        <div className={`${styles.banner} ${status === 'error' ? styles.error : styles.syncing}`}>
          {status === 'syncing' ? (
            <>
              <svg className={`${styles.icon} ${styles.spinning}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              <span className={styles.text}>{statusText}</span>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${total > 0 ? (completed / total) * 100 : 0}%` }}
                />
              </div>
            </>
          ) : (
            <>
              <svg className={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span className={styles.text}>{statusText}</span>
              <button className={styles.retryButton} onClick={() => triggerSync()}>
                Retry
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Small sync status indicator for header/toolbar
 */
export function SyncStatusBadge() {
  const { isOnline } = useOnlineStatus();
  const { status, total, completed } = useSyncStatus();

  if (isOnline && status === 'idle' && total === 0) {
    return null;
  }

  const pending = total - completed;

  return (
    <div className={styles.badge} title={!isOnline ? 'Offline' : status === 'syncing' ? 'Syncing...' : `${pending} pending`}>
      {!isOnline ? (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="1" y1="1" x2="23" y2="23" />
          <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
          <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
          <line x1="12" y1="20" x2="12.01" y2="20" />
        </svg>
      ) : status === 'syncing' ? (
        <svg className={styles.spinning} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
      ) : pending > 0 ? (
        <span className={styles.pendingCount}>{pending}</span>
      ) : null}
    </div>
  );
}

/**
 * Install PWA prompt component
 */
export function InstallPrompt() {
  const { isInstallable, promptInstall, dismissInstall } = usePWAInstall();

  if (!isInstallable) {
    return null;
  }

  return (
    <div className={styles.installPrompt}>
      <div className={styles.installContent}>
        <svg className={styles.installIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        <div className={styles.installText}>
          <strong>Install OHCS RMS</strong>
          <span>Add to home screen for quick access and offline use</span>
        </div>
      </div>
      <div className={styles.installActions}>
        <button className={styles.installButton} onClick={promptInstall}>
          Install
        </button>
        <button className={styles.dismissButton} onClick={dismissInstall}>
          Not now
        </button>
      </div>
    </div>
  );
}

/**
 * Hook for PWA installation
 */
function usePWAInstall() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);

  useEffect(() => {
    const handler = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setInstallPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handler as EventListener);

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstallable(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler as EventListener);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!installPrompt) return;

    const result = await installPrompt.prompt();
    console.log('Install prompt result:', result);

    setInstallPrompt(null);
    setIsInstallable(false);
  }, [installPrompt]);

  const dismissInstall = useCallback(() => {
    setIsInstallable(false);
    // Remember dismissal
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  }, []);

  return { isInstallable, promptInstall, dismissInstall };
}

// Type for beforeinstallprompt event
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<{ outcome: 'accepted' | 'dismissed' }>;
}
